import React, { useEffect, useState } from "react";
import {
  Input,
  Label,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { NAME_MAXLENGTH } from "@constants/constants";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { resetError, resetState } from "../controller/user-slice";
import { addUser, editUser, getUser } from "../services/user-service";
import { EMAIL_MAXLENGTH } from "@constants/constants";
import Select from "react-select";
import { getStateList } from "../../state/services.js/state-services";
import { getCountryList } from "../../country/services/country-services";
import { getPermisionList, getRoleList } from "../../roles/services/role-service";
import { getDistrictList } from "../../district/services/district-services";
import { userSchema } from "../../../helpers/validation-schema";
import CustomLabel from "../../../components/CustomLabel";
import Switch from "react-switch";
import { getPermissionGroupName, Offsymbol, OnSymbol } from "../../../helpers/helpers";
import ReactSwitch from "react-switch";

export const AddUser = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const userId = pathname.split("/")[3];
  const success = useSelector((state) => state.user.success);
  const user = useSelector((state) => state.user.users);
  const loading = useSelector((state) => state.user.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const successMessage = useSelector((state) => state.user.successMessage);
  const errorMessage = useSelector((state) => state.user.errorMessage);
  const states = useSelector((state) => state.state.states) || [];
  const roles = useSelector((state) => state.role.roles) || [];
  const countries = useSelector((state) => state.country.countries) || [];
  const districts = useSelector((state) => state.district.districts) || [];
  const permissions = useSelector((state) => state.permission.permissions) || [];
  const [permissionChecks, setPermissionChecks] = useState({});


  const options = {
    stateOptions: states.map((row) => ({ value: row._id, label: row.name })),
    countryOptions: countries.map((row) => ({
      value: row._id,
      label: row.name,
    })),
    districtOptions: districts.map((row) => ({
      value: row._id,
      label: row.name,
    })),
    roleOptions: roles.map((row) => ({ value: row._id, label: row.name })),
  };
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userSchema),
  });

  useEffect(() => {
    dispatch(getStateList({ pageCount: 1, limit: 100 }));
    dispatch(getRoleList({ pageCount: 1, limit: 100 }));
    dispatch(getCountryList({ pageCount: 1, limit: 100 }));
    dispatch(getDistrictList({ pageCount: 1, limit: 100 }));
    dispatch(getPermisionList({ pageCount: 1, limit: 100 }));
  }, []);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getUser(userId));
    }
  }, [userId, dispatch, route]);

  useEffect(() => {
    if (success) {
      navigate(`/users`);
      dispatch(resetState());
    }
  }, [success, dispatch, navigate, successMessage, pageQuery]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);

      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const message = item[fieldName];
        setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (user) {
      // Set form values based on user data
      setValue("short_name", user?.short_name);
      setValue("state_id", user?.state_id);
      setValue("district_id", user?.district_id);
      setValue("country_id", user?.country_id);
      setValue("email", user?.email);

      const userPermissions = user?.permission_ids || [];
      setPermissionChecks((prevChecks) => {
        const updatedChecks = {};
        userPermissions.forEach((permissionId) => {
          updatedChecks[permissionId] = true;
        });
        return updatedChecks;
      });

      setValue("permission_ids", userPermissions);
    }
  }, [user, setValue]);
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.group]) {
      acc[permission.group] = [];
    }
    acc[permission.group].push(permission);

    return acc;
  }, {});

  const handlePermissionToggle = (itemId, checked) => {
    setPermissionChecks((prevChecks) => {
      const updatedChecks = {
        ...prevChecks,
        [itemId]: checked,
      };

      const updatedPermissionIds = Object.keys(updatedChecks)
        .filter((key) => updatedChecks[key])
        .map((key) => key);

      setValue("permission_ids", updatedPermissionIds);
      return updatedChecks;
    });
  };

  const onSubmit = (data) => {
    if (route !== "edit") {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
      };
      dispatch(addUser(formattedData));
    } else {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
      };
      dispatch(editUser({ id: userId, data: formattedData }));
    }
  };
  const getCustomStyles = (field) => ({
    control: (provided, state) => ({
      ...provided,
      borderColor: errors[field] ? "red" : provided.borderColor,
      boxShadow: "none",
      maxHeight: "200px",
      overflowY: "auto",
      "&:hover": {
        borderColor: errors[field] ? "red" : provided.borderColor,
        boxShadow: "none",
        maxHeight: "200px",
        overflowY: "auto",
      },
    }),
  });

  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit User"
                  : route === "view"
                    ? "View User"
                    : "Create User"}
              </CardTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Short Name" isRequired>
                        Short Name
                      </CustomLabel>
                      <Controller
                        name="short_name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Enter Short Name"
                              invalid={!!errors.short_name}
                              // value={country?.name??""}
                              maxLength={NAME_MAXLENGTH}
                            />
                            {errors.short_name && (
                              <FormFeedback>
                                {errors.short_name.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Email" isRequired>
                        Email
                      </CustomLabel>
                      <Controller
                        name="email"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              type="email"
                              placeholder="Enter your email"
                              invalid={!!errors.email}
                              maxLength={EMAIL_MAXLENGTH}
                            />
                            {errors.email && (
                              <FormFeedback>
                                {errors.email.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Country" isRequired>
                        Country
                      </CustomLabel>
                      <Controller
                        name="country_id"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              {...field}
                              options={options.countryOptions}
                              placeholder="Select Country"
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={options.countryOptions.find(
                                (option) => option.value === field.value
                              )}
                              styles={getCustomStyles("country_id")}
                              isClearable
                            />
                            {errors.country_id && (
                              <FormFeedback className="d-block">
                                {errors.country_id.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="State" isRequired>
                        State
                      </CustomLabel>
                      <Controller
                        name="state_id"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              {...field}
                              options={options.stateOptions}
                              placeholder="Select State"
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={options.stateOptions.find(
                                (option) => option.value === field.value
                              )}
                              styles={getCustomStyles("state_id")}
                              isClearable
                            />
                            {errors.state_id && (
                              <FormFeedback className="d-block">
                                {errors.state_id.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="District" isRequired>
                        District
                      </CustomLabel>
                      <Controller
                        name="district_id"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              {...field}
                              options={options.districtOptions}
                              placeholder="Select District"
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={options.districtOptions.find(
                                (option) => option.value === field.value
                              )}
                              styles={getCustomStyles("district_id")}
                              isClearable
                            />
                            {errors.district_id && (
                              <FormFeedback className="d-block">
                                {errors.district_id.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  {route !== "edit" && (
                    <Col md={6}>
                      <FormGroup>
                        <CustomLabel htmlFor="Password" isRequired>
                          Password
                        </CustomLabel>
                        <Controller
                          name="password"
                          control={control}
                          defaultValue=""
                          render={({ field }) => (
                            <div>
                              <Input
                                {...field}
                                type="password"
                                placeholder="Enter your password"
                                invalid={!!errors.password}
                                maxLength={EMAIL_MAXLENGTH}
                              />
                              {errors.password && (
                                <FormFeedback>
                                  {errors.password.message}
                                </FormFeedback>
                              )}
                            </div>
                          )}
                        />
                      </FormGroup>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col md={7}>
                    <CustomLabel htmlFor="Name" isRequired>
                      Permissions
                    </CustomLabel>
                    {Object.keys(groupedPermissions).map((group) => (
                      <div key={group}>
                        <Row>
                          <Label className="fw-bold">{getPermissionGroupName(group)} :</Label>
                          {groupedPermissions[group].map((item) => (
                            <React.Fragment key={item._id}>
                              <Col md={2} className="text-center">
                                <Label
                                  onClick={() =>
                                    handlePermissionToggle(
                                      item._id,
                                      !permissionChecks[item._id]
                                    )
                                  }
                                  style={{ cursor: 'pointer' }}
                                >
                                  {item.name}
                                </Label>
                              </Col>
                              <Col md={2}>
                                <Controller
                                  name="permission_ids"
                                  control={control}
                                  defaultValue={[]}
                                  render={({ field }) => (
                                    <ReactSwitch
                                      id={item._id}
                                      uncheckedIcon={<Offsymbol />}
                                      checkedIcon={<OnSymbol />}
                                      className="me-1 mb-sm-8 mb-2"
                                      onColor="#02a499"
                                      checked={permissionChecks[item._id] || false}
                                      onChange={(checked) => handlePermissionToggle(item._id, checked)}
                                      height={20}
                                      width={50}
                                    />
                                  )}
                                />
                              </Col>
                            </React.Fragment>
                          ))}
                        </Row>
                      </div>
                    ))}
                    {errors.permission_ids && (
                      <FormFeedback className="d-block">
                        {errors.permission_ids.message}
                      </FormFeedback>
                    )}
                  </Col>
                </Row>

                <Row>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          navigate(`/users`);
                          dispatch(resetError());
                        }}
                      >
                        {route === "view" ? "Back" : "Cancel"}
                      </Button>
                      {route !== "view" ? (
                        <Button type="submit" color="primary">
                          {route === "edit" ? "Edit User" : "Create User"}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddUser;
